import React, { useState } from 'react';
import {
  ArrowRight,
  CheckCircle,
  Zap,
  Link2,
  Globe,
  Brain,
  TrendingUp,
  Settings,
  ChevronDown,
  ChevronUp,
  Shield,
  Code2,
  Radio,
  Workflow,
  Target,
  BarChart2,
  Clock,
  Server,
  AlertCircle,
  ChevronRight,
  Activity,
  Bot,
  Cpu,
  Play,
  Bell,
  ToyBrick,
  FastForward,
  PlugZap,
  Puzzle,
  CandlestickChart,
  Coins,
  Hammer,
  MonitorPlay,
  UserCheck,
  ZapOff,
  Crosshair,
  Timer,
} from 'lucide-react';
import './AlgoSignals.scss';
import './Faq.scss';
import Header from './Header';
import Footer from './Footer';
import { Link } from 'react-router-dom';

// Broker Logos
import zerodhaLogo from '../../../../assets/img/login-img/kite-logo.png';
import dhanLogo from '../../../../assets/img/login-img/dhan.jpg';
import fyersLogo from '../../../../assets/img/login-img/Logo-fyers-final.webp';
import upstoxLogo from '../../../../assets/img/login-img/upstock.webp';
import angeloneLogo from '../../../../assets/img/login-img/angel_booking.webp';
import xtsLogo from '../../../../assets/img/login-img/xts.png';
import shoonyaLogo from '../../../../assets/img/login-img/shoonya-logo.png';
import flattradeLogo from '../../../../assets/img/login-img/flattrade.jpg';
import aliceblueLogo from '../../../../assets/img/login-img/alice.webp';
import fivepaisaLogo from '../../../../assets/img/login-img/5paisa.webp';

import tradingViewLogo from '../../../../assets/img/logo/trading-view.svg';
import pythonLogo from '../../../../assets/img/logo/python.svg';
import amibrokerLogo from '../../../../assets/img/logo/ami-broker.png';
import ninjatraderLogo from '../../../../assets/img/logo/ninjatrader.png';
import mt4mt5Logo from '../../../../assets/img/logo/mt4-mt5.png';

const signalsIn = [
  { icon: <img src={tradingViewLogo} className='as-indicator-img' alt="TradingView" />, label: 'TradingView', sub: 'Alerts & Indicators' },
  { icon: <img src={pythonLogo} className='as-indicator-img' alt="Python" />, label: 'Python', sub: 'Custom Quant Models' },
  { icon: <img src={amibrokerLogo} className='as-indicator-img' alt="AmiBroker" />, label: 'AmiBroker', sub: 'AFL-based Strategies' },
  { icon: <img src={ninjatraderLogo} className='as-indicator-img' alt="NinjaTrader" />, label: 'NinjaTrader', sub: 'Strategy & Signals' },
  { icon: <img src={mt4mt5Logo} className='as-indicator-img' alt="MT4/MT5" />, label: 'MT4/MT5', sub: 'Expert Advisors' },
  { icon: <Play size={18} />, label: 'Manual Triggers', sub: 'Other Robot / Script' },
];

const brokersOut = [
  { label: 'Zerodha', logo: zerodhaLogo },
  { label: 'Dhan', logo: dhanLogo },
  { label: 'FYERS', logo: fyersLogo },
  { label: 'Upstox', logo: upstoxLogo },
  { label: 'Angel One', logo: angeloneLogo },
  { label: 'XTS', logo: xtsLogo },
];

const checkItems = [
  { icon: <CheckCircle size={18} />, label: 'No coding required' },
  { icon: <CheckCircle size={18} />, label: 'Works with your favorite indicators & platforms' },
  { icon: <CheckCircle size={18} />, label: 'Lightning-fast execution' },
  { icon: <CheckCircle size={18} />, label: 'Multi-broker connectivity' },
  { icon: <CheckCircle size={18} />, label: 'Built for serious traders' },
];

const howItWorksSteps = [
  {
    step: '01',
    icon: <Bell size={28} />,
    title: 'Create Your Trading Alert',
    description:
      'Use TradingView, Chartink, or your preferred platform to define your entry and exit logic using any indicator or price condition.',
  },
  {
    step: '02',
    icon: <Cpu size={28} />,
    title: 'Connect to Algo Signals',
    description:
      'Paste your alert into Algo Signals and map your trade details — instrument, quantity, order type, and strategy direction.',
  },
  {
    step: '03',
    icon: <Zap size={28} />,
    title: 'Trades Execute Automatically',
    description:
      'When your alert triggers, Algo Signals instantly sends the order to your broker terminal. No manual intervention. No emotional trading.',
  },
];

const whyFeatures = [
  {
    icon: <ToyBrick size={24} />,
    title: 'Truly Code-Free Automation',
    desc: 'You focus on strategy. We handle the execution engine.',
  },
  {
    icon: <FastForward size={24} />,
    title: 'Ultra-Fast Signal Processing',
    desc: 'Designed for real-time markets where every second matters.',
  },
  {
    icon: <PlugZap size={24} />,
    title: 'Seamless Indicator Integration',
    desc: 'Works with TradingView, Chartink, and external strategy engines.',
  },
  {
    icon: <Puzzle size={24} />,
    title: 'Platform Agnostic',
    desc: 'Not locked to one charting tool or software.',
  },
  {
    icon: <Brain size={24} />,
    title: 'Remove Emotional Mistakes',
    desc: 'Let rules drive trades instead of fear or greed.',
  },
  {
    icon: <TrendingUp size={24} />,
    title: 'Built for Options & Futures Traders',
    desc: 'Perfect for intraday, positional, and systematic traders.',
  },
];

const whoIsItFor = [
  { icon: <CandlestickChart size={20} />, label: 'Indicator-based traders' },
  { icon: <Coins size={20} />, label: 'Options sellers and buyers' },
  { icon: <Timer size={20} />, label: 'Intraday system traders' },
  { icon: <Workflow size={20} />, label: 'Strategy builders' },
  { icon: <Code2 size={20} />, label: 'Quant traders using Python' },
  { icon: <MonitorPlay size={20} />, label: 'AmiBroker and MT4/MT5 users' },
  { icon: <UserCheck size={20} />, label: 'Semi-automated traders' },
];

const useCases = [
  'EMA crossover auto execution',
  'Breakout strategy automation',
  'Options buying signals',
  'Short straddle adjustments',
  'Momentum strategy execution',
  'Quant model execution',
  'Intraday options systems',
];

const signalSources = [
  { 
    icon: <img src={tradingViewLogo} alt="TradingView" />, 
    label: 'TradingView', 
    desc: 'Connect alerts from indicators like RSI, MACD, or custom Pine Scripts.' 
  },
  { 
    icon: <Radio size={24} />, 
    label: 'Chartink', 
    desc: 'Execute trades based on your technical scanners and live price alerts.' 
  },
  { 
    icon: <img src={amibrokerLogo} alt="AmiBroker" />, 
    label: 'AmiBroker', 
    desc: 'Automate AFL-based strategies with lightning-fast signal execution.' 
  },
  { 
    icon: <img src={pythonLogo} alt="Python" />, 
    label: 'Python / API', 
    desc: 'Bridge your custom quant models and data science scripts to the market.' 
  },
  { 
    icon: <img src={ninjatraderLogo} alt="NinjaTrader" />, 
    label: 'NinjaTrader', 
    desc: 'Seamlessly route signals from your NinjaTrader strategy builder.' 
  },
  { 
    icon: <img src={mt4mt5Logo} alt="MT4/MT5" />, 
    label: 'MT4 / MT5', 
    desc: 'Automate expert advisors (EAs) directly to Indian broker terminals.' 
  },
  { 
    icon: <AlertCircle size={24} />, 
    label: 'Webhook / Manual', 
    desc: 'Connect any system that supports webhooks or trigger trades manually.' 
  },
];

const brokers = [
  { label: 'Zerodha', logo: zerodhaLogo },
  { label: 'Dhan', logo: dhanLogo },
  { label: 'FYERS', logo: fyersLogo },
  { label: 'Upstox', logo: upstoxLogo },
  { label: 'Angel One', logo: angeloneLogo },
  { label: 'XTS', logo: xtsLogo },
  { label: 'Shoonya', logo: shoonyaLogo },
  { label: 'Flattrade', logo: flattradeLogo },
  { label: 'Alice Blue', logo: aliceblueLogo },
  { label: '5paisa', logo: fivepaisaLogo },
  { label: 'And more', logo: null },
];

const reliability = [
  { icon: <Server size={20} />, text: 'Server-side execution' },
  { icon: <Activity size={20} />, text: 'Handles high-frequency signals' },
  { icon: <Workflow size={20} />, text: 'Supports multiple strategies simultaneously' },
  { icon: <TrendingUp size={20} />, text: 'Designed for Indian derivatives markets' },
  { icon: <Clock size={20} />, text: 'Works even when your local system is off' },
];

const faqs = [
  {
    q: 'How does Algo Signals work?',
    a: 'Algo Signals acts as the bridge between your signal source and your broker. When your alert fires, our system receives it and immediately places the configured order in your connected broker account.',
  },
  {
    q: 'Can I use signals from any platform?',
    a: 'Yes. Algo Signals is platform-agnostic. You can connect signals from TradingView, Chartink, AmiBroker, Python models, NinjaTrader, MT4/MT5, and manual triggers.',
  },
  {
    q: 'Do I need coding knowledge?',
    a: 'No. Algo Signals is designed to be completely code-free for most users. Advanced traders can also integrate custom logic if needed.',
  },
  {
    q: 'How fast is execution?',
    a: 'Algo Signals is built for low-latency routing. Orders are typically pushed within milliseconds after the alert is received, subject to broker and exchange conditions.',
  },
  {
    q: 'Can I connect multiple brokers?',
    a: 'Yes. You can manage and execute across multiple supported brokers from a single pipeline.',
  },
  {
    q: 'Does it work for options trading?',
    a: 'Absolutely. Algo Signals is widely used for options buying, selling, and multi-leg strategies.',
  },
  {
    q: 'What if my internet disconnects?',
    a: 'Your trades are handled on the server side. Once alerts are configured and active, your local internet connection is not required for execution.',
  },
  {
    q: 'Is there a limit on number of signals?',
    a: 'There is no practical limit. You can run multiple strategies simultaneously based on your plan and broker capacity.',
  },
  {
    q: 'Is Algo Signals suitable for beginners?',
    a: 'Yes. Beginners can start with simple alert-based automation, while advanced traders can deploy complex systematic strategies.',
  },
];

const AlgoSignals = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="algo-signals poppins">
      <Header />
      <section className="as-hero">
        <div className="as-container">
          <h1>
            Automate Your Trades from <br />{' '}
            <span>Indicators in Minutes</span>
          </h1>
          <p className="as-hero-sub">
            Stop watching charts all day. Let your strategy execute automatically.
          </p>
          <p className="as-hero-desc">
            <strong>Algo Signals</strong> connects your TradingView, Chartink, or any strategy engine
            directly to your broker so trades fire instantly when your conditions are met.
          </p>

          <div className="as-pill-grid">
            {checkItems.map(({ icon, label }) => (
              <div key={label} className="as-pill">
                <span className="as-pill-icon">{icon}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <Link to="/pricing" className="as-btn as-btn-primary">
            Get Started Now <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="as-flowchart">
        <div className="as-container">
          <div className="as-flow-headline">
            <span>One Signal Pipeline. Multiple Brokers. Full Control.</span>
          </div>
          <p className="as-flow-sub">
            Connect your strategies to real execution
          </p>

          <div className="as-flow-diagram">

            {/* LEFT — Signals In */}
            <div className="as-flow-panel">
              <div className="as-flow-panel-label">Signals In</div>
              <div className="as-flow-panel-nodes">
                {signalsIn.map(({ icon, label, sub }) => (
                  <div key={label} className="as-flow-src-card">
                    <span className="as-flow-src-icon">{icon}</span>
                    <div>
                      <div className="as-flow-src-title">{label}</div>
                      <div className="as-flow-src-sub">{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="as-flow-panel-note">Use your existing systems. No lock-in.</p>
            </div>

            {/* ARROW left → center */}
            <div className="as-flow-connector">
              <div className="as-flow-connector-line" />
              <ChevronRight size={22} className="as-flow-connector-head" />
            </div>

            {/* CENTER — Engine */}
            <div className="as-flow-engine">
              <div className="as-flow-engine-icon"><Cpu size={30} /></div>
              <div className="as-flow-engine-name"><span className="quik">Quik</span><span className="trade">Trade</span></div>
              <div className="as-flow-engine-label">Execution &amp; Control Layer</div>
              <div className="as-flow-engine-desc">Strategy-agnostic · Broker-connected · Built for Scale</div>
            </div>

            {/* ARROW center → right */}
            <div className="as-flow-connector">
              <div className="as-flow-connector-line" />
              <ChevronRight size={22} className="as-flow-connector-head" />
            </div>

            {/* RIGHT — Brokers Out */}
            <div className="as-flow-panel">
              <div className="as-flow-panel-label">Orders Out</div>
              <div className="as-flow-panel-nodes">
                {brokersOut.map((b) => (
                  <div key={b.label} className="as-flow-src-card">
                    <span className="as-flow-src-icon">
                      <img src={b.logo} alt={b.label} />
                    </span>
                    <div>
                      <div className="as-flow-src-title">{b.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="as-flow-panel-note">* More integrations supported</p>
            </div>
          </div>
        </div>
      </section>
      <section className="as-how">
        <div className="as-container">
          <div className="as-section-header">
            <h2>How Algo Signals Works</h2>
            <p>Automating your strategy is simpler than you think.</p>
          </div>

          <div className="as-steps">
            {howItWorksSteps.map(({ step, icon, title, description }, idx) => (
              <React.Fragment key={step}>
                <div className="as-step">
                  <div className="as-step-num">{step}</div>
                  <div className="as-step-icon">{icon}</div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
                {idx < howItWorksSteps.length - 1 && (
                  <div className="as-step-connector">
                    <ArrowRight size={24} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
      
      <section className="as-why">
        <div className="as-container">
          <div className="as-section-header">
            <h2>Why Traders Choose Algo Signals</h2>
          </div>
          <div className="as-grid-3">
            {whyFeatures.map(({ icon, title, desc }) => (
              <div key={title} className="as-card">
                <div className="as-card-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="as-who">
        <div className="as-container">
          <div className="as-section-header">
            <h2>Who Is This For?</h2>
            <p>
              Algo Signals is built for every type of systematic trader - whether you're just
              starting or running complex multi-leg strategies.
            </p>
          </div>

          {/* Audience cards */}
          <div className="as-who-cards">
            {whoIsItFor.map(({ icon, label }) => (
              <div key={label} className="as-who-card">
                <div className="as-who-card-icon">{icon}</div>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <p className="as-who-tagline">
            <CheckCircle size={18} />
            If you generate alerts, you can automate them.
          </p>

          {/* Use cases strip */}
          <div className="as-usecases">
            <div className="as-usecases-header">
              <h3>Example Use Cases</h3>
            </div>
            <div className="as-usecases-grid">
              {useCases.map((item) => (
                <div key={item} className="as-usecase-chip">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      <section className="as-sources">
        <div className="as-container">
          <div className="as-section-header">
            <h2>Supported Signal Sources</h2>
            <p>Algo Signals works with multiple signal generation platforms</p>
          </div>
          <div className="as-sources-grid">
            {signalSources.map(({ icon, label, desc }) => (
              <div key={label} className="as-source-card">
                <div className="as-source-header">
                  <span className="as-source-icon">{icon}</span>
                  <h3>{label}</h3>
                </div>
                <p>{desc}</p>
              </div>
            ))}
          </div>
          <p className="as-sources-tagline">
            <ArrowRight size={16} /> One pipeline for all your strategies.
          </p>
        </div>
      </section>

      <section className="as-brokers">
        <div className="as-container">
          <div className="as-section-header">
            <h2>Multi-Broker Execution</h2>
            <p>Execute seamlessly across leading brokers from a single control layer.</p>
          </div>
          <div className="as-brokers-grid">
            {brokers.map((b) => (
              <div
                key={b.label}
                className={`as-broker-item ${!b.logo ? 'as-broker-item-more' : ''}`}
              >
                {b.logo ? (
                  <>
                    <div className="as-broker-icon">
                      <img src={b.logo} alt={b.label} />
                    </div>
                    <span className="as-broker-label">{b.label}</span>
                  </>
                ) : (
                  <span className="as-broker-label-more">And more integrations supported</span>
                )}
              </div>
            ))}
          </div>
          <p className="as-brokers-note">No need to build separate systems for each broker.</p>
        </div>
      </section>

      <section className="as-reliability">
        <div className="as-container">
          <div className="as-section-header">
            <h2>
               Built for Scale and Reliability
            </h2>
          </div>
          <div className="as-reliability-grid">
            {reliability.map(({ icon, text }) => (
              <div key={text} className="as-reliability-item">
                <span className="as-rel-icon">{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="faq">
        <div className="container">
          <div className="header">
            <h2>Frequently Asked Questions</h2>
            <p>Got questions? We've got answers</p>
          </div>
          <div className="faq-list">
            {faqs.map(({ q, a }, i) => (
              <div key={i} className="faq-item">
                <button
                  className="faq-button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <div className="button-content">
                    <span className="question">{q}</span>
                    <svg
                      className={`icon ${openFaq === i ? 'open' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M12 6v12M6 12h12"
                      />
                    </svg>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="faq-answer">
                    <p>{a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


      <section className="as-cta">
        <div className="as-container">
          <h2>
            Stop manual execution.
            <br />
            Start systematic trading.
          </h2>

          <div className="as-cta-checks">
            {['Set alerts', 'Connect once', 'Trade automatically', 'Scale across brokers'].map(
              (t) => (
                <span key={t} className="as-cta-check-item">
                  <CheckCircle size={18} />
                  {t}
              </span>
              )
            )}
          </div>

          <Link to="/pricing" className="as-btn as-btn-secondary">
            Create Your First Signal <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlgoSignals;
